// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { Suspense, lazy } from "react";
import { Helmet } from "react-helmet-async";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { config, features, ossKey } from "@/lib/config";
import { getCompetitor } from "@/config/password-manager-competitors";
import { Header } from "@/components/landing/Header";
import { CompetitorHero } from "@/components/landing/compare";
import { SectionTransition } from "@/lib/motion";

const AtAGlanceCard = lazy(() => import("@/components/landing/compare/AtAGlanceCard").then((m) => ({ default: m.AtAGlanceCard })));
const HonestyCallout = lazy(() => import("@/components/landing/compare/HonestyCallout").then((m) => ({ default: m.HonestyCallout })));
const ComparisonTable = lazy(() => import("@/components/landing/compare/ComparisonTable").then((m) => ({ default: m.ComparisonTable })));
const CompetitorPricing = lazy(() => import("@/components/landing/compare/CompetitorPricing").then((m) => ({ default: m.CompetitorPricing })));
const AngleSection = lazy(() => import("@/components/landing/compare/AngleSection").then((m) => ({ default: m.AngleSection })));
const MigrationSteps = lazy(() => import("@/components/landing/compare/MigrationSteps").then((m) => ({ default: m.MigrationSteps })));
const CompetitorFAQ = lazy(() => import("@/components/landing/compare/CompetitorFAQ").then((m) => ({ default: m.CompetitorFAQ })));
const CompetitorCTA = lazy(() => import("@/components/landing/compare/CompetitorCTA").then((m) => ({ default: m.CompetitorCTA })));
const RelatedLinks = lazy(() => import("@/components/landing/compare/RelatedLinks").then((m) => ({ default: m.RelatedLinks })));
const CompetitorStructuredData = lazy(() => import("@/components/landing/compare/CompetitorStructuredData").then((m) => ({ default: m.CompetitorStructuredData })));
const Footer = lazy(() => import("@/components/landing/Footer").then((m) => ({ default: m.Footer })));

const COMPETITOR_ID = "lastpass" as const;
const SUPPORTED_LANGS = ["en", "ar", "fr", "de", "es"];

export const Route = createFileRoute("/vs-lastpass")({
  component: VsLastPassPage,
});

function VsLastPassPage() {
  const competitor = getCompetitor(COMPETITOR_ID);
  const siteUrl = config.siteUrl;
  const { t } = useTranslation("landing");
  const pageUrl = `${siteUrl}/${competitor.slug}`;

  return (
    <>
      <Helmet>
        <title>{t("compare.lastpass.meta.title", "pssmngr vs LastPass (2026) — Compare Features & Pricing")}</title>
        <meta name="description" content={t(ossKey("compare.lastpass.meta.description"), { defaultValue: "Compare pssmngr and LastPass: open-source security, modern encryption, and self-hosting vs a household name. See how they stack up." })} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={t("compare.lastpass.meta.title", "pssmngr vs LastPass (2026)")} />
        <meta property="og:description" content={t(ossKey("compare.lastpass.meta.description"), { defaultValue: "Compare pssmngr and LastPass side by side." })} />
        <meta property="og:image" content={`${siteUrl}/og/og-vs-lastpass.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("compare.lastpass.meta.title", "pssmngr vs LastPass (2026)")} />
        <meta name="twitter:description" content={t(ossKey("compare.lastpass.meta.description"), { defaultValue: "Compare pssmngr and LastPass side by side." })} />
        <meta name="twitter:image" content={`${siteUrl}/og/og-vs-lastpass.png`} />
        <link rel="canonical" href={pageUrl} />
        {SUPPORTED_LANGS.map((lang) => (
          <link key={lang} rel="alternate" hrefLang={lang} href={`${pageUrl}?lang=${lang}`} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={pageUrl} />
      </Helmet>

      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <Header />
        <main className="flex-1 flex flex-col">
          <CompetitorHero competitorId={COMPETITOR_ID} competitorName={competitor.displayName} />

          <Suspense fallback={null}>
            <SectionTransition><AtAGlanceCard competitor={competitor} /></SectionTransition>
            <SectionTransition><HonestyCallout competitorId={COMPETITOR_ID} competitorName={competitor.displayName} /></SectionTransition>
            <SectionTransition><ComparisonTable competitorId={COMPETITOR_ID} competitorName={competitor.displayName} /></SectionTransition>
          </Suspense>

          <Suspense fallback={null}>
            {features.payments && (
              <SectionTransition><CompetitorPricing competitor={competitor} /></SectionTransition>
            )}
            <SectionTransition><AngleSection competitorId={COMPETITOR_ID} /></SectionTransition>
            <SectionTransition><MigrationSteps competitorId={COMPETITOR_ID} competitorName={competitor.displayName} /></SectionTransition>
          </Suspense>

          <Suspense fallback={null}>
            <SectionTransition><CompetitorFAQ competitorId={COMPETITOR_ID} /></SectionTransition>
            <SectionTransition><CompetitorCTA /></SectionTransition>
            <SectionTransition><RelatedLinks currentCompetitorId={COMPETITOR_ID} /></SectionTransition>
          </Suspense>
        </main>

        <Suspense fallback={null}>
          <Footer />
          <CompetitorStructuredData competitorId={COMPETITOR_ID} competitor={competitor} />
        </Suspense>
      </div>
    </>
  );
}
